import { VisibilityPlugin } from '@goodboydigital/astro';
import { Container, Renderer } from 'pixi.js';
import { Signal } from 'typed-signals';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { Scene25D } from '../display/odie25d/Scene25D';
import { View25DSystem } from '../display/odie25d/View25DSystem';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { GameScreen } from '../screens/GameScreen';
import { SmoothTime } from '../utils/SmoothTime';
import { PlayerComponent } from './components/PlayerComponent';
import { EffectsSystem } from './effects/EffectsSystem';
import { VehicleEntity } from './entities/VehicleEntity';
import { PhysicsSystem } from './physics';
import { PhysicsDrawSystem } from './physics/PhysicsDrawSystem';
import { ReplaySystem } from './replay';
import { CameraSystem } from './systems/CameraSystem';
import { CheckpointSystem } from './systems/CheckpointSystem';
import { ChunkSystem } from './systems/ChunkSystem';
import { DebugSystem } from './systems/DebugSystem';
import { GarageSystem } from './systems/GarageSystem';
import { HUDSystem } from './systems/HUDSystem';
import { LevelSystem } from './systems/LevelSystem';
import { RaceSystem } from './systems/RaceSystem';
import { ScenarioSystem } from './systems/ScenarioSystem';
import { ShopSystem } from './systems/ShopSystem';
import { SoundSystem } from './systems/SoundSystem';
import { SpineDebugSystem } from './systems/SpineDebugSystem';
import { TutorialSystem } from './systems/TutorialSystem';
import { TweenSystem } from './systems/TweenSystem';

export interface GameOptions
{
    app: App;
    stage: Container;
    renderer: Renderer;
    screen: GameScreen;
}

export const DepthOrder = {
    TRACK: 0,
    OBSTACLE: 0.00001,
    TRAILS: 0.00002,
    VEHICLE: 0.00003,
    COIN: 0.00004,
    EFFECT: 0.1,
};

export const GHOST_COLOR = 0x79bfd9;
export const GHOST_COLOR_MIX = 0.4;

export class Game extends Scene25D
{
    public app: App;
    public stage: Container;
    public renderer: Renderer;
    public screen: GameScreen;
    public playing = false;
    public debug = false;

    // Systems
    public effects!: EffectsSystem;
    public physics!: PhysicsSystem;
    public camera!: CameraSystem;
    public level!: LevelSystem;
    public chunks!: ChunkSystem;
    public hud!: HUDSystem;
    public scenario!: ScenarioSystem;
    public checkpoint!: CheckpointSystem;
    public garage!: GarageSystem;
    public replay!: ReplaySystem;
    public race!: RaceSystem;
    public shop!: ShopSystem;
    public soundSystem!: SoundSystem;
    public tween!: TweenSystem;
    public tutorial!: TutorialSystem;

    public spineDebug!: SpineDebugSystem;

    /** Main vehicle, the only playable in the game */
    public vehicle!: VehicleEntity;

    /** The player who controls the main vehicle */
    public player!: PlayerComponent | null;

    /** Fire after game initialises, with all systems and main vehicle; happens only once */
    public onInit = new Signal<() => void>();

    /** Fire when game becomes idle */
    public onIdle = new Signal<() => void>();

    /** Fire on tap to play, before gameplay starts */
    public onPlay = new Signal<() => void>();

    /** Fire on game pause */
    public onPause = new Signal<() => void>();

    /** Fire on game resume */
    public onResume = new Signal<() => void>();

    /** Fire when player's vehicle crash */
    public onCrash = new Signal<() => void>();

    constructor(options: GameOptions)
    {
        super(options.stage);

        this.app = options.app;
        this.stage = options.stage;
        this.renderer = options.renderer;
        this.screen = options.screen;

        // Override scene's time with one that can smooth deltas, to prevent some frame rate fluctuation hiccups
        (this as any).time = new SmoothTime({ smooth: 0.2 });

        this.player = null;
        Shared.game = this;
    }

    /** View container for game specifc UI stuff, like HUD */
    public get uiView(): Container
    {
        return this.screen.gameView;
    }

    /** View container for game specifc UI stuff, like HUD */
    public get sceneView(): View25DSystem
    {
        return this.view25d;
    }

    /** Init the game, should be called only once */
    public init(): void
    {
        // Prevent double init, just in case
        if (this.physics) throw new Error('[Game] Game was already initialised');

        // Add essential systems
        this.physics = this.addSystem(PhysicsSystem as any);
        this.scenario = this.addSystem(ScenarioSystem as any);
        this.race = this.addSystem(RaceSystem as any);
        this.camera = this.addSystem(CameraSystem as any);
        this.level = this.addSystem(LevelSystem as any);
        this.tutorial = this.addSystem(TutorialSystem as any);
        this.chunks = this.addSystem(ChunkSystem as any);
        this.checkpoint = this.addSystem(CheckpointSystem as any);
        this.hud = this.addSystem(HUDSystem as any);
        this.replay = this.addSystem(ReplaySystem as any);
        this.soundSystem = this.addSystem(SoundSystem as any);
        this.garage = this.addSystem(GarageSystem as any);
        this.shop = this.addSystem(ShopSystem as any);
        this.tween = this.addSystem(TweenSystem as any);
        this.effects = this.addSystem(EffectsSystem as any);

        // Add optional debug-related systems
        if (Settings.debug)
        {
            this.debug = true;
            this.addSystem(PhysicsDrawSystem as any);
            this.addSystem(DebugSystem as any);
        }

        if (Settings.spineDebug)
        {
            this.spineDebug = this.addSystem(SpineDebugSystem as any);
            this.scenario.mountScenario('city');
        }
        else
        {
            // Initialises the main player and vehicle
            this.setupPlayer();
            this.idle(true);

            if (Settings.rec) this.app.user.deleteBestRuns();

            // Pre-define user starting race
            if (Settings.race >= 0) Shared.user.data.race = Settings.race;

            // Connect pause on visibility shift
            this.app.get(VisibilityPlugin).onVisibilityChange.connect((visibile: boolean) =>
            {
                if (!visibile && !this.race.idle && !this.vehicle.finished)
                {
                    Shared.game.pause();
                    Shared.game.soundSystem.onPause();
                    this.hud.settingsButton.setExpandTarget(1, true);
                }
            });

            // SIgnal initialised
            this.onInit.emit();
        }
    }

    /** Ensure vehicle and player added to the game */
    protected setupPlayer(): void
    {
        // Ensure vehicle is created
        if (!this.vehicle)
        {
            this.vehicle = new VehicleEntity();
            this.vehicle.onCrash.connect(() => this.onVehicleCrash());
            this.vehicle.onFinishRace.connect(() => this.onVehicleFinishRace());
            this.garage.updateVehicle();
        }
        this.vehicle.selfAddToGame();
        this.vehicle.trailLeft.snap();
        this.vehicle.trailRight.snap();

        // Prevent recording while cheating
        if (Settings.god) this.replay.rec = false;

        this.player = this.vehicle.getComponent(PlayerComponent) || this.vehicle.addComponent(PlayerComponent);
        this.player.reset();
    }

    /** Set idle state, for being in background of other things  */
    public idle(force = false): void
    {
        if (!this.playing && !force) return;

        this.hud.messageWidget.close();
        this.scenario.mountScenario('city');
        this.resume();
        this.level.levelClear();
        this.level.mountIdleLevel();
        this.app.analytics.baseData.race_number = "nvs";
        this.vehicle.reset();
        this.vehicle.respawn(800, -80, 0, false);
        this.camera.resetTarget({x: 0, y: -80}, 0.06);
        this.vehicle.motor.turnOff();
        this.vehicle.motor.idleRun();
        this.setupPlayer();
        this.vehicle.character.spine.playCelebrate();
        
        if(this.vehicle.tag){
            this.vehicle.tag.turnOff();
        }
        
        this.playing = false;
        this.onIdle.emit();
        this.race.updateVehiclesOrder();
    }

    /** Prepare the game to play, do eventual animation then start gameplay  */
    public play(): void
    {
        if (!this.level.chunks.length) this.idle();

        this.physics.reset();

        const raceIndex = Shared.user.data.race;

        if (Settings.randomGear) this.garage.randomiseVehicle(true);

        this.race.setupRace(raceIndex);
        this.level.mountLevelForRace(raceIndex);
        this.camera.setTarget(this.vehicle.chassis.physics, true);
        this.vehicle.motor.turnOn();
        this.vehicle.character.spine.playIdle();
        
        if(this.vehicle.tag){
            this.vehicle.tag.turnOn();
        }
        
        this.setupPlayer();
        this.playing = true;
        this.onPlay.emit();

        this.soundSystem.registerVehicle('player', {
            chassisId: this.garage.selectedChassis,
            wheelLeftId: this.garage.selectedWheelLeft,
            wheelRightId: this.garage.selectedWheelRight,
        });

        this.soundSystem.startRace();
        this.race.updateVehiclesOrder();
        
        this.app.analytics.baseData.race_number = this.race.raceIndex;
        
        this.camera.cameraModifier.y = 0;
        
        if(this.race.raceIndex == 0)
        {
            this.app.analytics.trackState(AnalyticsState.TUTORIAL);
            this.app.analytics.trackEvent(AnalyticsEvent.TUTORIAL_START);
        }
        else
        {
            this.app.analytics.trackState(AnalyticsState.RACE_PLAY);
            this.app.analytics.trackEvent(AnalyticsEvent.GAME_LEVEL_START);
        }
        
    }

    /** Pause the game  */
    public pause(): void
    {
        if (!this.playing) return;
        this.paused = true;
        this.onPause.emit();
    }

    /** Resume the game  */
    public resume(): void
    {
        if (!this.playing) return;
        this.paused = false;
        this.onResume.emit();
    }

    /** Listen to main vehicle crash, then move it back to last checkpoint */
    private onVehicleCrash(): void
    {
        // this.camera.setTarget(this.vehicle.character.physics);
        this.onCrash.emit();

        // If race has not started yet, vehicle can respawn at the same place
        if (this.race.idle)
        {
            this.checkpoint.resetCheckpoint({
                x: Math.min(this.vehicle.chassis.physics.x, this.level.courseStartX - 500),
                y: this.level.courseStartY - 100,
            });
        }

        if (!this.vehicle.finished)
        {
            this.hud.messageWidget.showCrash();
            this.physics.timer.dispatchAfter(2, () =>
            {
                this.level.levelRespawn();
                this.checkpoint.goToLastCheckpoint();
            });
            this.soundSystem.playSfx('crash');
        }
    }

    /** Listen to main vehicle finish the ace */
    private onVehicleFinishRace(): void
    {
        this.hud.finishPosition.present(this.race.position, this.race.players, 0.5);
        this.race.finishRace();
        this.camera.setTarget(this.vehicle.character);
        this.camera.zoomModifier = 0.05;

        // Replay keeps recording for 2 seconds after finish, to give it some time to stabilize.
        // This timeout + screen transition will prevent player to reset the game before recording finishes
        // Otherwise, recording just stop and no replay gets uploaded

        if (this.app.rank.didRankUp())
        {
            setTimeout(() => this.app.controller.toRank(), 3000);
        }
        else
        {
            setTimeout(() => this.app.controller.toResults(), 3000);
        }

        this.soundSystem.raceEnd();
    }
}
